import React from 'react';
import { RotateCcw } from 'lucide-react';
import MultiPicker from './MultiPicker';
import { DATE_PRESETS, datePreset } from '../poPayment';

export const EMPTY_FILTERS = {
  preset: 'this_fy', from: datePreset('this_fy').from, to: datePreset('this_fy').to,
  clients: [], categories: [], vendors: [], payment: [], approval: [], min: '', max: '',
};

const PAYMENT_OPTS = [
  { key: 'paid', label: 'Paid' }, { key: 'pending', label: 'Pending' },
  { key: 'overdue', label: 'Overdue' }, { key: 'cancelled', label: 'Cancelled' },
];
const APPROVAL_OPTS = [
  { key: 'approved', label: 'Approved' }, { key: 'pending', label: 'Awaiting approval' },
  { key: 'not_submitted', label: 'Not submitted' }, { key: 'rejected', label: 'Rejected' },
];

const Field = ({ label, children }) => (
  <div className="min-w-0">
    <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);

export default function POAnalyticsFilters({ filters, setFilters, options }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const applyPreset = (key) => set({ preset: key, ...datePreset(key) });
  const toOpts = (arr) => (arr || []).map((x) => (typeof x === 'string' ? { key: x, label: x } : x));

  return (
    <div className="sticky top-0 z-20 bg-white/85 backdrop-blur-lg rounded-2xl border border-slate-200/70 shadow-sm p-4 mb-6 space-y-3" data-testid="po-analytics-filter-bar">
      <div className="flex flex-wrap items-center gap-1.5">
        {DATE_PRESETS.map((p) => (
          <button key={p.key} type="button" onClick={() => applyPreset(p.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors active:scale-95 ${filters.preset === p.key ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white text-slate-700 border-slate-200 hover:border-fuchsia-300'}`}
            data-testid={`po-analytics-preset-${p.key}`}>{p.label}</button>
        ))}
        <div className="flex items-center gap-1.5 ml-1">
          <input type="date" value={filters.from} onChange={(e) => set({ preset: 'custom', from: e.target.value })}
            className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white" data-testid="po-analytics-date-from" />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={filters.to} onChange={(e) => set({ preset: 'custom', to: e.target.value })}
            className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white" data-testid="po-analytics-date-to" />
        </div>
        <button type="button" onClick={() => setFilters(EMPTY_FILTERS)}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-fuchsia-700 px-2 py-1.5 rounded-lg hover:bg-fuchsia-50"
          data-testid="po-analytics-reset"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <Field label="Clients"><MultiPicker label="Clients" options={toOpts(options?.clients)} value={filters.clients} onChange={(v) => set({ clients: v })} testId="po-analytics-clients" /></Field>
        <Field label="Categories"><MultiPicker label="Categories" options={toOpts(options?.categories)} value={filters.categories} onChange={(v) => set({ categories: v })} testId="po-analytics-categories" /></Field>
        <Field label="Vendors"><MultiPicker label="Vendors" options={toOpts(options?.vendors)} value={filters.vendors} onChange={(v) => set({ vendors: v })} testId="po-analytics-vendors" /></Field>
        <Field label="Payment"><MultiPicker label="Statuses" options={PAYMENT_OPTS} value={filters.payment} onChange={(v) => set({ payment: v })} testId="po-analytics-payment" /></Field>
        <Field label="Approval"><MultiPicker label="Approvals" options={APPROVAL_OPTS} value={filters.approval} onChange={(v) => set({ approval: v })} testId="po-analytics-approval" /></Field>
        <Field label="Min amount (₹)">
          <input type="number" min="0" value={filters.min} onChange={(e) => set({ min: e.target.value })} placeholder="0"
            className="h-9 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="po-analytics-min-amount" />
        </Field>
        <Field label="Max amount (₹)">
          <input type="number" min="0" value={filters.max} onChange={(e) => set({ max: e.target.value })} placeholder="No limit"
            className="h-9 w-full px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="po-analytics-max-amount" />
        </Field>
      </div>
      {filters.payment.length === 0 && (
        <p className="text-[11px] text-slate-500">Cancelled POs are left out unless you pick them under Payment.</p>
      )}
    </div>
  );
}
